import React from "react";
import { TranslateContext } from "../../context/TranslateContext";
import { GlobalContext } from "../../context/GlobalContext";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { CreditCardOutlined, StarOutlined } from "@mui/icons-material";

import StripeComp from "./gateways/StripeComp";
import PayPalComp from "./gateways/PayPalComp";
import RazorpayComp from "./gateways/RazorpayComp";
import PaystackComp from "./gateways/PaystackComp";
import MercadoPagoComp from "./gateways/MercadoPagoComp";
import OfflineCheckout from "./gateways/OfflineCheckout";
import { formatPlanPriceInDisplay, resolvePlanDisplayCurrency } from "../../utils/planDisplayCurrency";
import { trackInitiateCheckout } from "../../common/tracking";

export const GATEWAY_COMPONENTS = {
  stripe: StripeComp,
  paypal: PayPalComp,
  razorpay: RazorpayComp,
  paystack: PaystackComp,
  mercadopago: MercadoPagoComp,
};

function planNum(p, snake, camel) {
  const v = p?.[snake] ?? p?.[camel];
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Plan checkout: summary + payment gateways. Used by `/checkout/:id` and inline on Billing.
 * Plan amounts are in USD; each gateway shows/charges in its settlement currency (from admin + market rates).
 *
 * @param {{ planId: string | number; maxWidth?: number }} props
 */
export function CheckoutPanel({ planId, maxWidth = 480 }) {
  const { lang } = React.useContext(TranslateContext);
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const hitAxiosRef = React.useRef(hitAxios);

  const [plan, setPlan] = React.useState(null);
  const [gateways, setGateways] = React.useState({});
  const [offlineHtml, setOfflineHtml] = React.useState("");
  const [offlineActive, setOfflineActive] = React.useState(false);
  const [paying, setPaying] = React.useState("");
  const [loadError, setLoadError] = React.useState("");
  const trackedPlan = React.useRef(null);

  const id = planId != null ? String(planId).trim() : "";

  React.useEffect(() => {
    hitAxiosRef.current = hitAxios;
  }, [hitAxios]);

  React.useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setPlan(null);
    setLoadError("");

    (async () => {
      try {
        const [planRes, gwRes, offlineRes] = await Promise.all([
          hitAxiosRef.current({
            path: "/api/plan/get_plan_by_id",
            admin: false,
            post: true,
            obj: { id },
            showLoading: false,
          }),
          hitAxiosRef.current({
            path: "/api/payment/active-gateways",
            admin: false,
            post: false,
            showLoading: false,
          }),
          hitAxiosRef.current({
            path: "/api/payment/offline-details",
            admin: false,
            post: false,
            showLoading: false,
          }),
        ]);

        if (cancelled) return;
        if (planRes?.data?.success) setPlan(planRes.data.data);
        else setLoadError(planRes?.data?.msg || "Plan not found.");

        if (gwRes?.data?.success) setGateways(gwRes.data.data);
        if (offlineRes?.data?.success) {
          setOfflineHtml(offlineRes.data.html || "");
          setOfflineActive(!!offlineRes.data.active);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || "Could not load checkout.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const activeGateways = Object.entries(gateways).filter(
    ([key, val]) => val.active && GATEWAY_COMPONENTS[key],
  );

  const { currency: displayCurrency, isSinglePaystackNgn } = resolvePlanDisplayCurrency(gateways);

  const priceUsdNum = parseFloat(String(plan?.price ?? "0")) || 0;
  const mainDisplayStr = plan ? formatPlanPriceInDisplay(plan.price, displayCurrency) : "0";
  const strikeRaw = plan?.price_strike ?? plan?.priceStrike;
  const strikeUsdNum = strikeRaw ? parseFloat(String(strikeRaw)) : NaN;
  const strikeDisplayStr =
    plan && Number.isFinite(strikeUsdNum) && strikeUsdNum > priceUsdNum
      ? formatPlanPriceInDisplay(strikeRaw, displayCurrency)
      : null;

  React.useEffect(() => {
    if (!plan?.id || trackedPlan.current === String(plan.id)) return;
    trackedPlan.current = String(plan.id);
    trackInitiateCheckout({
      value: Number(mainDisplayStr) || 0,
      currency: displayCurrency.code || "USD",
    });
  }, [displayCurrency.code, mainDisplayStr, plan]);

  const discount = strikeDisplayStr
    ? Math.round(
        ((parseFloat(String(strikeRaw)) - priceUsdNum) / parseFloat(String(strikeRaw))) * 100,
      )
    : null;

  if (!id) {
    return (
      <Typography variant="body2" color="text.secondary">
        {lang?.billingCheckoutInvalid || "Invalid plan."}
      </Typography>
    );
  }

  if (loadError) {
    return (
      <Typography variant="body2" color="error">
        {loadError}
      </Typography>
    );
  }

  if (!plan?.id) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        overflow: "visible",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
          <CreditCardOutlined sx={{ color: "primary.main" }} />
          <Typography fontWeight={700} variant="subtitle1">
            {lang?.checkout || "Checkout"}
          </Typography>
        </Box>
        {(plan.popular === 1 || plan.popular === "1") && (
          <Chip
            icon={<StarOutlined fontSize="inherit" />}
            label={lang?.popular || "Popular"}
            size="small"
            color="primary"
            sx={{ fontWeight: 700, fontSize: "0.7rem" }}
          />
        )}
      </Box>

      <Box p={3}>
        <Stack spacing={3}>
          <Stack spacing={1.5}>
            <Typography variant="h5" fontWeight={800}>
              {plan.title}
            </Typography>

            <Stack direction="row" alignItems="baseline" gap={1.5} flexWrap="wrap">
              <Typography variant="h4" fontWeight={800} color="primary">
                {displayCurrency.symbol}
                {mainDisplayStr}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {displayCurrency.code}
              </Typography>

              {strikeDisplayStr && (
                <Typography variant="body1" color="text.disabled" sx={{ textDecoration: "line-through" }}>
                  {displayCurrency.symbol}
                  {strikeDisplayStr} {displayCurrency.code}
                </Typography>
              )}

              {discount ? (
                <Chip label={`${discount}% OFF`} size="small" color="success" sx={{ fontWeight: 700, fontSize: "0.7rem" }} />
              ) : null}
            </Stack>

            <Divider />

            {[
              {
                label: lang?.credits || "Credits",
                value: planNum(plan, "credits", "credits").toLocaleString(),
              },
              {
                label: lang?.maxChars || "Max Characters",
                value: planNum(plan, "max_characters", "maxCharacters").toLocaleString(),
              },
              {
                label: lang?.creditValidity || "Credit validity",
                value: lang?.creditsNeverExpire || "Never expire",
              },
            ].map(({ label, value }) => (
              <Stack key={label} direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <Typography
              variant="caption"
              fontWeight={700}
              letterSpacing={1}
              textTransform="uppercase"
              color="text.secondary"
            >
              {lang?.selectPayment || "Select Payment Method"}
            </Typography>

            {activeGateways.length === 0 && !offlineActive && (
              <Typography variant="body2" color="text.secondary">
                {lang?.noGateways || "No payment methods available at the moment."}
              </Typography>
            )}

            {priceUsdNum <= 0 ? (
              <Typography variant="body2" color="error">
                {lang?.paidPlanRequired || "This plan is no longer available."}
              </Typography>
            ) : (
              <>
                {activeGateways.map(([key, gwData]) => {
                  const GatewayComp = GATEWAY_COMPONENTS[key];
                  return (
                    <GatewayComp
                      key={key}
                      plan={plan}
                      gwData={gwData}
                      paying={paying}
                      setPaying={setPaying}
                      {...(key === "paystack" ? { hideChargedAsLine: isSinglePaystackNgn } : {})}
                    />
                  );
                })}

                {offlineActive && offlineHtml && (
                  <OfflineCheckout
                    plan={plan}
                    currency={displayCurrency}
                    html={offlineHtml}
                    paying={paying}
                    setPaying={setPaying}
                    lang={lang}
                  />
                )}
              </>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
